<template>
  <v-card elevation="0">
    <template v-if="documentForDialog">
      <warehouse-purchase-order-dialog
        v-model="purchaseOrderDialog"
        :editedItem="documentForDialog"
        :warehouse="documentForDialog?.warehouse || {}"
        :is-purchase-order="documentForDialog?.transaction_type === 'order'"
      >
      </warehouse-purchase-order-dialog>
      <warehouse-entry-grocery-dialog
        v-model="purchaseItemDialog"
        :warehouse="documentForDialog?.warehouse || {}"
        :editedItem="documentForDialog"
      >
      </warehouse-entry-grocery-dialog>
      <warehouse-waybill-dialog
        v-model="waybillDialog"
        :warehouse="documentForDialog?.warehouse || {}"
        :edited-item="documentForDialog"
      >
      </warehouse-waybill-dialog>
      <warehouse-write-off-dialog
        v-model="writeOffDialog"
        :warehouse="documentForDialog?.warehouse || {}"
        :edited-item="documentForDialog"
      >
      </warehouse-write-off-dialog>
    </template>

    <!-- Mobile View -->
    <template v-if="device.isMobile">
      <template v-if="filteredTransactions.length > 0 || loading">
        <!-- Show grouped by warehouse if multiple warehouses selected -->
        <template v-if="filters.warehouse.length > 1">
          <mobile-data-cards
            v-for="(group, warehouseId) in transactionsByWarehouse"
            :key="warehouseId"
            :headers="mobileHeaders"
            :items="group.items"
            :loading="loading"
            :title="group.warehouseName"
            item-key="id"
            show-filters
            :fields-per-row="null"
            :show-row-number="false"
            :bold-fields="['grocery.name']"
            @click="openDocumentDialog($event)"
          >
            <template v-slot:item.transaction_type="{ item }">
              <v-chip
                small
                :color="documentTypes[item.transaction_type]?.color"
                text-color="white"
              >
                {{ documentTypes[item.transaction_type]?.title }}
              </v-chip>
            </template>
            <template v-slot:item.created_at="{ item }">
              {{
                localeDate(new Date(item.created_at), {
                  year: "numeric",
                  month: "2-digit",
                  day: "2-digit",
                  hour: "2-digit",
                  minute: "2-digit",
                })
              }}
            </template>
          </mobile-data-cards>
        </template>
        <!-- Show single list if only one warehouse selected -->
        <template v-else>
          <mobile-data-cards
            :headers="mobileHeaders"
            :items="filteredTransactions"
            :loading="loading"
            item-key="id"
            show-filters
            :fields-per-row="null"
            :show-row-number="false"
            :bold-fields="['grocery.name']"
            @click="openDocumentDialog($event)"
          >
            <template v-slot:item.transaction_type="{ item }">
              <v-chip
                small
                :color="documentTypes[item.transaction_type]?.color"
                text-color="white"
              >
                {{ documentTypes[item.transaction_type]?.title }}
              </v-chip>
            </template>
            <template v-slot:item.created_at="{ item }">
              {{
                localeDate(new Date(item.created_at), {
                  year: "numeric",
                  month: "2-digit",
                  day: "2-digit",
                  hour: "2-digit",
                  minute: "2-digit",
                })
              }}
            </template>
          </mobile-data-cards>
        </template>
      </template>

      <empty-state
        v-if="!loading && filteredTransactions.length === 0"
        icon="mdi-history"
        :title="$t('no_transactions')"
        :subtitle="$t('no_transactions_yet')"
      />
    </template>

    <!-- Desktop View -->
    <template v-else>
      <v-data-table
        v-if="filteredTransactions.length > 0 || loading"
        :headers="headers"
        :items="filteredTransactions"
        :loading="loading"
        class="striped-table ordera-box-shadow"
      >
        <template v-slot:item.transaction_type="{ item }">
          <td
            :style="{
              backgroundColor: documentTypes[item.transaction_type]?.color,
            }"
          >
            {{ documentTypes[item.transaction_type]?.title }}
          </td>
        </template>
        <template v-slot:item.created_at="{ item }">
          {{
            localeDate(item.created_at, {
              year: "numeric",
              month: "2-digit",
              day: "2-digit",
              hour: "2-digit",
              minute: "2-digit",
            })
          }}
        </template>
        <template v-slot:item.actions="{ item }">
          <circle-button
            icon="mdi-eye"
            :tooltip="$t('overview')"
            color="primary"
            @click="openDocumentDialog(item)"
          />
        </template>
      </v-data-table>

      <empty-state
        v-if="!loading && filteredTransactions.length === 0"
        icon="mdi-history"
        :title="$t('no_transactions')"
        :subtitle="$t('no_transactions_yet')"
      />
    </template>
  </v-card>
</template>

<script>
import hasPrinting from "@/mixins/hasPrinting";
import hasExcel from "@/mixins/hasExcel";
import hasWarehousePrinting from "@/mixins/warehouse/hasWarehousePrinting";
import {
  WarehousePurchaseOrderDialog,
  WarehouseWaybillDialog,
} from "@/components/warehouse/requirement";
import WarehouseWriteOffDialog from "@/components/warehouse/writeOff/dialogs/WarehouseWriteOffDialog.vue";
import WarehouseEntryGroceryDialog from "@/components/warehouse/entry/WarehouseEntryGroceryDialog.vue";
import EmptyState from "@/components/global/EmptyState.vue";
import MobileDataCards from "@/components/global/MobileDataCards.vue";
import hasSnackbar from "@/mixins/hasSnackbar";
import warehouseService from "@/services/warehouse";

export default {
  mixins: [hasPrinting, hasWarehousePrinting, hasExcel, hasSnackbar],
  props: {
    selectedGrocery: {
      type: Object,
      default: null,
    },
  },
  components: {
    WarehousePurchaseOrderDialog,
    WarehouseWaybillDialog,
    WarehouseWriteOffDialog,
    WarehouseEntryGroceryDialog,
    EmptyState,
    MobileDataCards,
  },
  data() {
    return {
      transactions: [],
      loading: false,
      documentForDialog: null,
      purchaseOrderDialog: false,
      purchaseItemDialog: false,
      waybillDialog: false,
      writeOffDialog: false,
      allDocuments: [],
    };
  },
  methods: {
    async load() {
      this.loading = true;
      try {
        const res = await warehouseService.getTransactions();
        this.transactions = res.data;
        this.$store.commit("warehouse/SET_TRANSACTIONS", this.transactions);
      } catch (error) {
        this.showErrorSnackbar(this.$t("error_loading_transactions"));
      } finally {
        this.loading = false;
      }
    },
    async openDocumentDialog(item) {
      try {
        if (!this.allDocuments.length) {
          const { data } = await warehouseService.getWarehouseDocuments({});
          this.allDocuments = data;
        }
        this.documentForDialog = this.allDocuments.find(
          (doc) => doc.id === item.warehouse_document_id
        );
        if (!this.documentForDialog) {
          this.showErrorSnackbar(this.$t("document_not_found"));
          return;
        }
        if (item.transaction_type === "order" || item.transaction_type === "request") {
          this.purchaseOrderDialog = true;
        } else if (item.transaction_type === "entry") {
          this.purchaseItemDialog = true;
        } else if (item.transaction_type === "exit") {
          this.waybillDialog = true;
        } else if (item.transaction_type === "write_off") {
          this.writeOffDialog = true;
        }
      } catch (error) {
        this.showErrorSnackbar(this.$t("error_loading_document"));
      }
    },
  },
  computed: {
    translatedTransactions() {
      return this.transactions.map((item) => {
        return {
          ...item,
          warehouse: {
            ...item.warehouse,
            name: this.translate(item.warehouse?.name || ""),
          },
          grocery: {
            ...item.grocery,
            name: this.translate(item.grocery?.name),
            measure: this.translate(item.grocery?.measure),
          },
        };
      });
    },
    filteredTransactions() {
      return this.customSearch(
        this.translatedTransactions,
        this.headers,
        this.filters.search
      ).filter((item) => {
        const transactionFilter =
          !this.filters.documentType ||
          item.transaction_type === this.filters.documentType;
        const groceryFilter =
          !this.filters.groceryCode ||
          item.grocery.code === this.filters.groceryCode.code;
        const { from, to } = this.filters.dates;
        const dateEnd = to + " 23:59:59";
        const dateFilter =
          !from ||
          !to ||
          (new Date(item.created_at) >= new Date(from) &&
            new Date(item.created_at) <= new Date(dateEnd));
        const warehouseFilter = this.filters.warehouse.some(
          (warehouse) => warehouse.id === item.warehouse_id
        );
        return transactionFilter && groceryFilter && dateFilter && warehouseFilter;
      });
    },
    headers() {
      const baseHeaders = [
        {
          text: "ID",
          align: "start",
          value: "id",
          mobile: false,
        },
      ];

      // Only show warehouse header if multiple warehouses are selected
      if (this.filters.warehouse.length > 1) {
        baseHeaders.push({
          text: this.$t("stock"),
          align: "start",
          value: "warehouse.name",
          cols: 6,
          mobile: false,
        });
      }

      baseHeaders.push(
        {
          text: this.$t("warehouse.document_number"),
          value: "warehouse_document_id",
          mobile: false,
        },
        { text: this.$t("warehouse.transaction"), value: "transaction_type", cols: 6 },
        { text: this.$t("code"), value: "grocery.code", cols: 4, mobile: false },
        { text: this.$t("warehouse.product"), value: "grocery.name", cols: 6 },
        {
          text: this.$t("quantity"),
          value: "amount",
          cols: 6,
          suffixField: "grocery.measure",
        },
        { text: this.$t("unit_of_measurment"), value: "grocery.measure", mobile: false },
        { text: this.$t("date"), value: "created_at", print: false, cols: 6 },
        {
          text: this.$t("actions"),
          value: "actions",
          sortable: false,
          align: "center",
          mobile: false,
          print: false,
          width: "80px",
        }
      );

      return baseHeaders;
    },
    mobileHeaders() {
      return this.headers.filter((h) => h.mobile !== false);
    },
    transactionsByWarehouse() {
      const groups = {};
      this.filteredTransactions.forEach((item) => {
        const warehouseId = item.warehouse_id;
        if (!groups[warehouseId]) {
          groups[warehouseId] = {
            warehouseName: item.warehouse?.name || this.$t("warehouse.unknown"),
            items: [],
          };
        }
        groups[warehouseId].items.push(item);
      });
      return groups;
    },
    filters() {
      return this.$store.state.warehouse.filters;
    },
    documentTypes() {
      return {
        order: {
          title: this.$t("warehouse.purchase_order"),
          color: "#4CAF50",
        },
        request: {
          title: this.$t("warehouse.request_quote"),
          color: "#81C784",
        },
        entry: {
          title: this.$t("warehouse.warehouse_replenishment"),
          color: this.$vuetify.theme.currentTheme.primary,
        },
        exit: {
          title: this.$t("warehouse.shipping_bill"),
          color: "#2196F3",
        },
        write_off: {
          title: this.$t("warehouse.write_off"),
          color: this.$vuetify.theme.currentTheme.error || "#F44336",
        },
      };
    },
    exportItems() {
      return this.filteredTransactions.map((item) => {
        return {
          ...item,
          transaction_type: this.documentTypes[item.transaction_type].title,
        };
      });
    },
    exportTitle() {
      return [
        this.filters.warehouse.map((warehouse) => warehouse.name).join(", ") ||
          this.$t("stock"),
        this.$t("warehouse.transactions"),
      ].join(" ");
    },
  },
  watch: {
    "$store.state.warehouse.refreshTrigger"() {
      this.load();
    },
    "filters.dates": {
      handler() {
        this.load();
      },
      deep: true,
      immediate: true,
    },
    isPrinting(isPrinting) {
      if (isPrinting) {
        this.printDocument({
          title: this.exportTitle,
          subtitle: this.subtitle,
          tables: [
            {
              headers: this.headers,
              items: this.exportItems,
            },
          ],
        });
      }
    },
    isExcelDownloading() {
      if (this.isExcelDownloading) {
        this.downloadExcel({
          fileName: this.exportTitle + ".xlsx",
          sheets: [
            {
              name: this.exportTitle,
              headers: this.headers,
              items: this.exportItems,
              title: this.exportTitle,
              subtitle: this.subtitle,
            },
          ],
        });
      }
    },
  },
};
</script>
